import { useState, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'deal-engine-column-config';
const MASTER_PRICE_LIST_STORAGE_KEY = 'master-price-list-column-config';

/** Wider identity columns for Master Price List catalog readability. */
export const MASTER_PRICE_LIST_WIDTH_OVERRIDES = {
  name: { defaultWidth: 400, minWidth: 200, maxWidth: 600 },
  category: { defaultWidth: 120, minWidth: 80, maxWidth: 220 },
  qty: { defaultWidth: 70, minWidth: 48, maxWidth: 100 },
};

// Column definitions — single source of truth for table structure
export const COLUMNS = [
  { id: 'name',        label: 'Name',        group: 'identity', defaultWidth: 150, minWidth: 90,  maxWidth: 360, resizable: true,  hideable: false, align: 'left',   tooltip: 'Line item description — identifies the deliverable or cost item' },
  { id: 'category',    label: 'Cat',         group: 'identity', defaultWidth: 90,  minWidth: 60,  maxWidth: 220, resizable: true,  hideable: true,  align: 'left',   tooltip: 'Grouping category — used in vendor aggregation and reporting' },
  { id: 'qty',         label: 'Qty',         group: 'identity', defaultWidth: 62,  minWidth: 48,  maxWidth: 100, resizable: true,  hideable: true,  align: 'right',  tooltip: 'Quantity multiplier — applied to both revenue and cost per unit' },
  { id: 'rev_unit',    label: 'Rev/Unit',    group: 'revenue',  defaultWidth: 92,  minWidth: 74,  maxWidth: 150, resizable: true,  hideable: true,  align: 'right',  tooltip: 'Revenue per unit in native currency — converted to core currency for totals' },
  { id: 'rev_cur',     label: 'Cur',         group: 'revenue',  defaultWidth: 72,  minWidth: 66,  maxWidth: 90,  resizable: false, hideable: true,  align: 'center', tooltip: 'Revenue currency — converted to core currency at current FX rate' },
  { id: 'discount_type', label: 'Disc Type', group: 'revenue',  defaultWidth: 92,  minWidth: 82,  maxWidth: 120, resizable: false, hideable: true,  align: 'center', tooltip: 'Discount mode per line: none, percent, or fixed amount' },
  { id: 'discount_value', label: 'Disc',     group: 'revenue',  defaultWidth: 86,  minWidth: 72,  maxWidth: 120, resizable: true,  hideable: true,  align: 'right', tooltip: 'Discount input value. Percent uses %, amount uses line revenue currency.' },
  { id: 'rev_chf',     label: 'Rev Total',   group: 'revenue',  defaultWidth: 84,  minWidth: 68,  maxWidth: 130, resizable: true,  hideable: true,  align: 'right',  tooltip: 'Revenue in core currency — Qty x Rev/Unit x FX rate. This is the internal truth' },
  { id: 'cost_unit',   label: 'Cost/Unit',   group: 'cost',     defaultWidth: 92,  minWidth: 74,  maxWidth: 150, resizable: true,  hideable: true,  align: 'right',  tooltip: 'Cost per unit in native currency — converted to core currency for totals' },
  { id: 'cost_cur',    label: 'Cur',         group: 'cost',     defaultWidth: 72,  minWidth: 66,  maxWidth: 90,  resizable: false, hideable: true,  align: 'center', tooltip: 'Cost currency — converted to core currency at current FX rate' },
  { id: 'cost_chf',    label: 'Cost Total',  group: 'cost',     defaultWidth: 84,  minWidth: 68,  maxWidth: 130, resizable: true,  hideable: true,  align: 'right',  tooltip: 'Cost in core currency — Qty x Cost/Unit x FX rate. This is the internal truth' },
  { id: 'vendor',      label: 'Vendor',      group: 'cost',     defaultWidth: 86,  minWidth: 60,  maxWidth: 180, resizable: true,  hideable: true,  align: 'left',   tooltip: 'Vendor supplying this line item — aggregated in Vendors tab' },
  { id: 'margin_chf',  label: 'Margin',      group: 'margin',   defaultWidth: 80,  minWidth: 60,  maxWidth: 130, resizable: true,  hideable: true,  align: 'right',  tooltip: 'Margin in core currency — Revenue minus Cost' },
  { id: 'margin_pct',  label: '%',           group: 'margin',   defaultWidth: 52,  minWidth: 45,  maxWidth: 90,  resizable: true,  hideable: true,  align: 'right',  tooltip: 'Margin % — calculated from core currency values, independent of display currency' },
  { id: 'actions',     label: 'Actions',     group: 'actions',  defaultWidth: 68,  minWidth: 60,  maxWidth: 100, resizable: false, hideable: false, align: 'center', tooltip: null },
];

// Client view columns (inserted conditionally)
export const CLIENT_COLUMNS = [
  { id: 'client_rev_unit', label: 'Rev/Unit', group: 'client', defaultWidth: 80, minWidth: 60, maxWidth: 130, resizable: true,  hideable: true, align: 'right', tooltip: 'Revenue per unit in client presentation currency — derived from core currency' },
  { id: 'client_rev_total', label: 'Total Rev', group: 'client', defaultWidth: 80, minWidth: 60, maxWidth: 130, resizable: true,  hideable: true, align: 'right', tooltip: 'Total revenue in client currency — for client-facing quotes only' },
];

function columnDefsWithOverrides(widthOverrides = {}) {
  return [...COLUMNS, ...CLIENT_COLUMNS].map((col) => {
    const o = widthOverrides[col.id];
    if (!o) return col;
    return {
      ...col,
      defaultWidth: o.defaultWidth ?? col.defaultWidth,
      minWidth: o.minWidth ?? col.minWidth,
      maxWidth: o.maxWidth ?? col.maxWidth,
    };
  });
}

function normalizeWidths(raw = {}, widthOverrides = {}) {
  const defs = columnDefsWithOverrides(widthOverrides);
  const next = {};
  defs.forEach((c) => {
    const val = Number(raw[c.id]);
    if (Number.isFinite(val)) {
      next[c.id] = Math.max(c.minWidth, Math.min(c.maxWidth, val));
    } else {
      next[c.id] = c.defaultWidth;
    }
  });
  return next;
}

function loadConfigForKey(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveConfigForKey(storageKey, config) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(config));
  } catch {
    // Silent fail — localStorage might be full or blocked
  }
}

export function useColumnConfig(options = {}) {
  const storageKey = options.storageKey || STORAGE_KEY;
  const widthOverridesProp = options.widthOverrides;
  const widthOverrides = useMemo(() => widthOverridesProp || {}, [widthOverridesProp]);
  const columnDefs = useMemo(() => columnDefsWithOverrides(widthOverrides), [widthOverrides]);

  const saved = useMemo(() => loadConfigForKey(storageKey), [storageKey]);

  const [widths, setWidths] = useState(() => {
    return saved?.widths ? normalizeWidths(saved.widths, widthOverrides) : normalizeWidths({}, widthOverrides);
  });

  const [hidden, setHidden] = useState(() => {
    return new Set(saved?.hidden || []);
  });

  const persist = useCallback((nextWidths, nextHidden) => {
    saveConfigForKey(storageKey, {
      widths: nextWidths,
      hidden: Array.from(nextHidden),
    });
  }, [storageKey]);

  const setColumnWidth = useCallback((colId, width) => {
    const col = columnDefs.find((c) => c.id === colId);
    if (!col) return;
    const clamped = Math.max(col.minWidth, Math.min(col.maxWidth, width));
    setWidths(prev => {
      const next = { ...prev, [colId]: clamped };
      persist(next, hidden);
      return next;
    });
  }, [hidden, persist, columnDefs]);

  const toggleColumn = useCallback((colId) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(colId)) {
        next.delete(colId);
      } else {
        next.add(colId);
      }
      persist(widths, next);
      return next;
    });
  }, [widths, persist]);

  const isVisible = useCallback((colId) => {
    return !hidden.has(colId);
  }, [hidden]);

  const resetLayout = useCallback(() => {
    const defaults = {};
    columnDefs.forEach((c) => { defaults[c.id] = c.defaultWidth; });
    setWidths(defaults);
    setHidden(new Set());
    localStorage.removeItem(storageKey);
  }, [columnDefs, storageKey]);

  return { widths, hidden, setColumnWidth, toggleColumn, isVisible, resetLayout, columnDefs };
}

export function useMasterPriceListColumnConfig() {
  return useColumnConfig({
    storageKey: MASTER_PRICE_LIST_STORAGE_KEY,
    widthOverrides: MASTER_PRICE_LIST_WIDTH_OVERRIDES,
  });
}
